import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Instagram, Youtube, Facebook, Twitter as XIcon } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FadeIn } from '@/components/motion/FadeIn';
import { Skeleton } from '@/components/ui/Skeleton';
import { MarkdownWithAnchors } from '@/components/marketing/MarkdownRenderer';
import { ShareButtons } from '@/components/marketing/ShareButtons';
import { useContentBySlug } from '@/lib/cms';
import { formatDate } from '@/lib/utils';
import type { ContentCategory } from '@/lib/cms-types';

const CATEGORY_LABEL: Record<ContentCategory, string> = {
  'short-drama': 'Short drama',
  'micro-series': 'Micro-series',
  promo: 'Promo',
  'behind-the-scenes': 'Behind the scenes',
};

export function ContentDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: content, isLoading, isError } = useContentBySlug(slug);

  if (isLoading) {
    return (
      <>
        <Meta title="Loading…" path={`/contents/${slug ?? ''}`} />
        <div className="pt-24 sm:pt-28 md:pt-32">
          <Skeleton className="h-[60vh] w-full" />
        </div>
      </>
    );
  }

  if (isError || !content) {
    return (
      <>
        <Meta title="Series not found" path={`/contents/${slug ?? ''}`} />
        <section className="flex min-h-[70vh] items-center justify-center pt-20">
          <div className="container-narrow text-center">
            <h1 className="display-serif text-display-l text-fg">Series not found.</h1>
            <p className="mt-3 text-fg-muted">It may have been unpublished or moved.</p>
            <div className="mt-6">
              <Button asChild>
                <Link to="/contents">
                  <ArrowLeft className="h-4 w-4" /> All series
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  const canonicalUrl = `https://vonlof.co/contents/${content.slug}`;
  const inf = content.primary_influencer;
  const socials = [
    { href: content.tiktok_url, label: 'TikTok', Icon: TikTokGlyph },
    { href: content.instagram_url, label: 'Instagram', Icon: Instagram },
    { href: content.youtube_url, label: 'YouTube', Icon: Youtube },
    { href: content.x_url, label: 'X', Icon: XIcon },
  ].filter((s) => !!s.href) as Array<{ href: string; label: string; Icon: typeof Instagram }>;

  return (
    <>
      <Meta
        title={content.title}
        description={content.summary}
        path={`/contents/${content.slug}`}
        type="article"
        publishedTime={content.published_at ?? undefined}
        image={content.cover_url ?? undefined}
        jsonLd={
          {
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: content.title,
            description: content.summary,
            datePublished: content.published_at ?? undefined,
            image: content.cover_url ?? undefined,
            author: { '@type': 'Organization', name: 'Vonlof' },
            publisher: {
              '@type': 'Organization',
              name: 'Vonlof',
              logo: { '@type': 'ImageObject', url: 'https://vonlof.co/og/default.png' },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
          } as never
        }
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <article className="container-narrow">
          <FadeIn>
            <Link
              to="/contents"
              className="inline-flex items-center gap-1 text-sm text-fg-muted transition-colors hover:text-gold-500"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All series
            </Link>
          </FadeIn>
          <FadeIn delay={0.05}>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Badge variant="gold">{CATEGORY_LABEL[content.category]}</Badge>
              {content.published_at && (
                <span className="inline-flex items-center gap-1.5 text-xs text-fg-subtle">
                  <Calendar className="h-3.5 w-3.5" /> {formatDate(content.published_at)}
                </span>
              )}
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="mt-4 display-serif text-display-l text-fg">{content.title}</h1>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="mt-6 text-xl text-fg-muted">{content.summary}</p>
          </FadeIn>
        </article>

        {content.cover_url && (
          <section className="mt-12">
            <div className="container-wide">
              <FadeIn>
                <div className="aspect-[16/9] overflow-hidden rounded-xl border border-border bg-bg-subtle">
                  <img
                    src={content.cover_url}
                    alt={`${content.title} cover`}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="h-full w-full object-cover"
                  />
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* Body + influencer side rail */}
        <section className="mt-12">
          <div className="container-default grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              {content.description_md ? (
                <FadeIn>
                  <MarkdownWithAnchors content={content.description_md} />
                </FadeIn>
              ) : (
                <FadeIn>
                  <p className="text-fg-muted">More details coming soon.</p>
                </FadeIn>
              )}

              {socials.length > 0 && (
                <FadeIn delay={0.1}>
                  <Card className="mt-10 p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">
                      Follow the series
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {socials.map(({ href, label, Icon }) => (
                        <a
                          key={label}
                          href={href!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg"
                        >
                          <Icon className="h-4 w-4" /> {label}
                        </a>
                      ))}
                    </div>
                  </Card>
                </FadeIn>
              )}

              <FadeIn delay={0.15}>
                <div className="mt-8">
                  <ShareButtons url={canonicalUrl} title={content.title} />
                </div>
              </FadeIn>
            </div>
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                {inf ? (
                  <Card className="overflow-hidden">
                    <div className="aspect-square w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
                      {inf.portrait_url ? (
                        <img
                          src={inf.portrait_url}
                          alt={`${inf.name} portrait`}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    <div className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">
                        Featured talent
                      </p>
                      <h3 className="mt-2 display-serif text-2xl text-fg">{inf.name}</h3>
                      {inf.headline && <p className="mt-1 text-sm text-fg-muted">{inf.headline}</p>}
                      {inf.joined_at && (
                        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-fg-subtle">
                          <Calendar className="h-3.5 w-3.5" /> Joined {formatDate(inf.joined_at)}
                        </p>
                      )}
                      {inf.bio_md && (
                        <div className="prose prose-invert mt-4 max-w-none text-sm">
                          <MarkdownWithAnchors content={inf.bio_md} />
                        </div>
                      )}
                      <div className="mt-5 flex flex-wrap gap-2">
                        {inf.tiktok_url && (
                          <a href={inf.tiktok_url} target="_blank" rel="noopener noreferrer" aria-label={`${inf.name} on TikTok`} className="social-link">
                            <TikTokGlyph className="h-4 w-4" />
                          </a>
                        )}
                        {inf.instagram_url && (
                          <a href={inf.instagram_url} target="_blank" rel="noopener noreferrer" aria-label={`${inf.name} on Instagram`} className="social-link">
                            <Instagram className="h-4 w-4" />
                          </a>
                        )}
                        {inf.youtube_url && (
                          <a href={inf.youtube_url} target="_blank" rel="noopener noreferrer" aria-label={`${inf.name} on YouTube`} className="social-link">
                            <Youtube className="h-4 w-4" />
                          </a>
                        )}
                        {inf.facebook_url && (
                          <a href={inf.facebook_url} target="_blank" rel="noopener noreferrer" aria-label={`${inf.name} on Facebook`} className="social-link">
                            <Facebook className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </Card>
                ) : null}
              </div>
            </aside>
          </div>
        </section>
      </main>

      <style>{`.social-link{ @apply inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-bg-elevated text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg; }`}</style>
    </>
  );
}

/** TikTok is in lucide-react under a different name on some versions — use a
 * small inline glyph fallback so we never depend on a particular lucide build. */
function TikTokGlyph(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.81a8.16 8.16 0 0 0 4.77 1.52V6.9a4.85 4.85 0 0 1-1.84-.21z" />
    </svg>
  );
}